import { LOCAL_CONTACT_OPTIONS, PROPERTY_MANAGEMENT_OPTIONS } from "../components/payment/constants";
import parse from 'html-react-parser'

export const isNullUndefinedOrEmptyObject = (value) =>
    isNullOrUndefined(value) || (value && Object.keys(value).length === 0)
export const isNullUndefinedOrEmptyString = (value) =>
    isNullOrUndefined(value) || value === ''
export const isNullOrUndefined = (value) =>
    value === null || value === undefined

export const generate_uuidv4 = (useUnderscores = false) => {
    let uuid = '10000000-1000-4000-8000-100000000000'.replace(/[018]/g, (c) =>
        (
            +c ^
            (crypto.getRandomValues(new Uint8Array(1))[0] & (15 >> (+c / 4)))
        ).toString(16)
    );
    if (useUnderscores) return uuid.replace("-", "_");
    return uuid;
}

export const sanitizeAndParseConfigLink = (domNode) => {
    let sanitizedHref = stripBackslashesAndQuotes(domNode?.attribs?.href);
    let sanitizedReferrer = stripBackslashesAndQuotes(domNode?.attribs?.referrer);
    let sanitizedTarget = stripBackslashesAndQuotes(domNode?.attribs?.target);
    return {
        href: sanitizedHref,
        referrer: sanitizedReferrer,
        target: sanitizedTarget
    }
}

const stripBackslashesAndQuotes = (text) => {
    if (isNullUndefinedOrEmptyObject(text) || isNullUndefinedOrEmptyString(text)) return null;
    let backslashesStripped = text.split("\\").filter(r => !isNullUndefinedOrEmptyString(r))[0];
    let quotesStripped = backslashesStripped.split("\"").filter(r => !isNullUndefinedOrEmptyString(r))[0];
    let strippedString = !isNullUndefinedOrEmptyString(quotesStripped) ? quotesStripped : null;
    return strippedString;
}



// Use this instead of hasOwnProperty to avoid linter errors
export const objectHasProperty = (propertyName, obj) => {
    return propertyName in obj;
}

// Converts the input to a string with no white space and only alphanumeric characters
// If converted string is empty, returns an underscore separated uuid instead
export const convertStringToKey = (input) => {
    if (!input) return generate_uuidv4(true);
    const convertedKey = input.replace(/[^a-zA-Z0-9_\s]/g, '').replace(/\s/g, '_').toLowerCase();
    if (isNullUndefinedOrEmptyString(convertedKey)) return generate_uuidv4(true);
    return convertedKey;
}

export const unescapeConfigString = (escapedText) => {
    return escapedText.split("\\").join("");
}

export const convertNumberToCurrency = (number) => {
    if (typeof number === 'string') return number;
    if (!Number.isFinite(number)) return '';
    const formatter = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' });
    return formatter.format(number);
}

export const convertStringToCurrency = (input) => {
    let number = parseFloat(input);
    if (isNaN(number)) {
        // If parsing fails, return input as is
        return input;
    }
    return convertNumberToCurrency(number);
}

export const titleize = (string) => {
    return string
        .toLowerCase()
        .replace(/([a-zA-Z0-9']+)/g, word => (
            word.charAt(0).toUpperCase() + word.slice(1)
        ));
}

export const areObjectsEqual = (objA, objB) => {
    return JSON.stringify(objA) === JSON.stringify(objB);
}

export const getAddressWithUnitNumber = (address, unitNumber) => {
    const unitNumberString = !isNullUndefinedOrEmptyString(unitNumber) ? ` (Unit ${unitNumber})` : ""
    const fullAddress = `${address}${unitNumberString}`;
    return titleize(fullAddress);
}

export const beginsWithVowel = (text) => {
    if (isNullUndefinedOrEmptyString(text)) return false;
    const firstLetter = text[0];
    const vowels = ["a", "e", "i", "o", "u"];
    return vowels.includes(firstLetter.toLowerCase());
}

const hashCode = (str) => {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = (hash << 5) - hash + char;
        hash |= 0; // Convert to 32bit integer
    }
    return hash;
}

export const objectHash = (obj) => {
    const jsonString = JSON.stringify(obj);
    return hashCode(jsonString);
}

export const convertSnakeCaseToCamelCase = (text) => {
    let splitText = text.split("_");
    splitText.forEach(part => part[0].toUpperCase() + part.slice(1).toLowerCase());
    return splitText.join('');
}

export const determineSelectedManagementOption = (licensePeople) => {
    // Spreadsheet licneses may be missing data
    // If propertyManager data does not exists set to self_managed
    if (isNullUndefinedOrEmptyObject(licensePeople.propertyManager) || areObjectsEqual(licensePeople.registrant, licensePeople.propertyManager)) {
        return PROPERTY_MANAGEMENT_OPTIONS.SELF_MANAGED;
    }
    else {
        return PROPERTY_MANAGEMENT_OPTIONS.PROPERTY_MANAGER;
    }
}

export const determineSelectedOwnerRepOption = (licensePeople) => {
    const ownersArray = Array.isArray(licensePeople.owner) ? licensePeople.owner : [licensePeople.owner]; // Pages expect this to be an array, but towns that don't allows multiple owners return this as a single object

    if (areObjectsEqual(licensePeople.localContact, licensePeople.registrant)) {
        return LOCAL_CONTACT_OPTIONS.MYSELF;
    }
    else if (areObjectsEqual(licensePeople.localContact, ownersArray[0])) {
        return LOCAL_CONTACT_OPTIONS.OWNER;
    }
    else if (areObjectsEqual(licensePeople.localContact, licensePeople.propertyManager)) {
        return LOCAL_CONTACT_OPTIONS.PROPERTY_MANAGER;
    }
    else {
        return LOCAL_CONTACT_OPTIONS.OTHER;
    }
}

export const constructMarkup = (text, additionalLinkClass = null) => {
    if (text === undefined) return "";
    return parse(
        text,
        {
            replace: (domNode) => {
                if (domNode.name === 'a') {
                    let sanitizedLink = sanitizeAndParseConfigLink(domNode);
                    if (isNullUndefinedOrEmptyString(sanitizedLink.href)) return domNode;
                    return (
                        <a
                            href={sanitizedLink.href}
                            target={sanitizedLink.target}
                            rel={sanitizedLink.referrer}
                            className={["accessible-link-text", additionalLinkClass].filter(c => c !== null).join(" ")}
                        >
                            {domNode.children[0].data}
                        </a>
                    )
                }

                if (domNode.type === 'text') domNode.data = unescapeConfigString(domNode.data);
                return domNode
            }
        }
    )
}

export const parseArbitraryStringToHtmlElementId = (text) => {
    if (text.length === 0) return text;
    return ("attr-" + text).split(' ').join('-').toLowerCase();
}

